import type { ComboboxOption } from "@sentricmusic/ui";

import { Button, Combobox, FormInput, Heading, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@sentricmusic/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSpotify } from "react-icons/fa";

import { useUIStateStore } from "@/store/uiStateStore";
import { extractSpotifyArtistId } from "@/utils/spotifyUrlUtils";

import { useUpdateArtistAccount, useUpdateArtistAccountMeta } from "../api/mutations";
import { useGetArtistMeta, useGetMyAccount, useGetUserMeta } from "../api/queries";
import { apiCall } from "../api/shared";
import { sendEvent } from "../helpers/helpers";
import useAppStore from "../store/appStateZustand";
import FormError from "./FormError";

type SocialSpotifyProps = {
  closeForm: () => void;
};

type SpotifyArtistImage = {
  url: string;
  height?: number;
  width?: number;
};

type SpotifyArtistDto = {
  id: string;
  name: string;
  images?: SpotifyArtistImage[];
  spotifyUrl?: string;
};

type LegacySpotifyArtist = {
  id: string;
  name: string;
  images: SpotifyArtistImage[];
  external_urls?: { spotify: string };
};

type SpotifyArtistSearchResponse = {
  artists: SpotifyArtistDto[];
};

type SpotifyArtistGetResponse = {
  artist: SpotifyArtistDto;
};

const spotifyActiveTabStyle = {
  backgroundColor: "#1DB954",
  opacity: 1,
  color: "black",
  textDecoration: "underline",
};

function ArtistCardSkeleton({ isTunecore }: { isTunecore: boolean }) {
  return (
    <div
      style={
        isTunecore
          ? { background: "linear-gradient(to bottom right, #a6a6a6, #505050)" }
          : { background: "#FFF" }
      }
      className="rounded-lg shadow-lg flex mt-4 flex-col sm:w-80"
    >
      <div className="mx-4 mt-4">
        <Skeleton className="w-full h-40 rounded-lg max-h-52" />
      </div>
      <div className="flex flex-col gap-4 p-4">
        <Skeleton className="self-center w-40 h-8" />
        <div className="flex flex-col-reverse items-center justify-between gap-4 md:flex-row md:gap-0">
          <Skeleton className="w-20 h-9" />
          <Skeleton className="w-32 h-9" />
        </div>
      </div>
    </div>
  );
}

function mapArtistDtoToLegacy(dto: SpotifyArtistDto): LegacySpotifyArtist {
  return {
    id: dto.id,
    name: dto.name,
    images: dto.images ?? [],
    ...(dto.spotifyUrl != null && { external_urls: { spotify: dto.spotifyUrl } }),
  };
}

function SocialSpotify({ closeForm }: SocialSpotifyProps) {
  const { t } = useTranslation(["songs", "common"]);
  const flags = useFlags();
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const artistMeta = useGetArtistMeta({ aac: selectedAccount });
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const userMeta = useGetUserMeta();
  const accountMetaMutation = useUpdateArtistAccountMeta();
  const accountMutation = useUpdateArtistAccount();

  const isTunecore = useUIStateStore(state => state.isTunecore);

  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const [pending, setPending] = useState(false);

  const [result, setResult] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [spotifyURL, setSpotifyURL] = useState("");
  const [fetchMethod, setFetchMethod] = useState<"searchWithName" | "searchWithURL" | null>(null);

  const [activeTab, setActiveTab] = useState("searchWithURL");
  const [artistOptions, setArtistOptions] = useState<ComboboxOption[]>([]);
  const [isSearchingArtists, setIsSearchingArtists] = useState(false);
  const [selectedArtistId, setSelectedArtistId] = useState<string | undefined>(undefined);
  const [isLoadingArtistDetails, setIsLoadingArtistDetails] = useState(false);

  const parsedResult = useMemo<LegacySpotifyArtist | null>(() => {
    if (result == null)
      return null;
    try {
      return JSON.parse(result) as LegacySpotifyArtist;
    }
    catch (error) {
      console.warn("[SocialSpotify] Invalid JSON in result:", result, error);
      return null;
    }
  }, [result]);

  let title = t("songs:link-spotify-artist-title");
  let desc = t("songs:link-spotify-artist-subtitle");
  switch (userMeta.data?.role) {
    case "Manager":
      title = t("songs:link-spotify-manager-title");
      desc = t("songs:link-spotify-manager-subtitle");
      break;
    default:
      break;
  }

  const addSpotify = (): void => {
    if (myAccount.data == null || parsedResult == null)
      return;

    setMutationErrorMessage("");
    setValidationErrorMessage("");
    setPending(true);

    const fetchMethodForHubspot = fetchMethod;

    const data = {
      account_id: selectedAccount,
      facebook_obj: artistMeta.data?.facebook_obj,
      spotify_obj: result,
      songkick_obj: artistMeta.data?.songkick_obj,
      twitter_obj: artistMeta.data?.twitter_obj,
      bandsintown_obj: artistMeta.data?.bandsintown_obj,
      instagram_obj: artistMeta.data?.instagram_obj,
    };

    accountMetaMutation.reset();
    accountMetaMutation.mutate(data, {
      onSuccess: (response) => {
        if (response.failedvalidation) {
          setValidationErrorMessage(response.messages?.join(", ") ?? "");
          setPending(false);
        }
        else {
          void artistMeta.refetch();
          setPending(false);
          closeForm();
          if (fetchMethodForHubspot === "searchWithName") {
            sendEvent({ property: "pe14489857_spotify_account_name" });
          }
          else if (fetchMethodForHubspot === "searchWithURL") {
            sendEvent({ property: "pe14489857_spotify_account_link" });
          }
        }
      },
      onError: (error: Error) => {
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });

    const rightsAppArtistAccount = {
      cataloguecode: myAccount.data.cataloguecode,
      artistaccountcode: myAccount.data.artistaccountcode,
      accountname: myAccount.data.accountname,
      facebookpageurl: myAccount.data.facebookpageurl,
      twitterprofileurl: myAccount.data.twitterprofileurl,
      soundcloudpageurl: myAccount.data.soundcloudpageurl,
      spotifyid: parsedResult.id,
      releasedtoserviceorstore: myAccount.data.releasedtoserviceorstore,
      performedlastsixmonths: myAccount.data.performedlastsixmonths,
      flagid: myAccount.data.flagid,
      etag: myAccount.data.etag,
    };

    accountMutation.reset();
    accountMutation.mutate(rightsAppArtistAccount, {
      onSuccess: (response) => {
        if (response.failedvalidation) {
          setValidationErrorMessage(response.messages?.join(", ") ?? "");
          setPending(false);
        }
        else {
          void artistMeta.refetch();
          setPending(false);
          closeForm();
        }
      },
      onError: (error: Error) => {
        console.error("error", error);
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  const handleArtistSearch = useCallback((query: string): void => {
    if (query === "") {
      setArtistOptions([]);
      return;
    }
    setIsSearchingArtists(true);
    apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/spotify/search?q=${encodeURIComponent(query)}&type=artist`,
    })
      .then((response: SpotifyArtistSearchResponse) => {
        setFetchMethod("searchWithName");
        setArtistOptions(response.artists.map(dto => ({
          value: dto.id,
          label: dto.name,
        })));
      })
      .catch((error: unknown) => {
        console.error("Error searching Spotify artists:", error);
      })
      .finally(() => setIsSearchingArtists(false));
  }, []);

  const handleSelectArtist = useCallback((artistId: string): void => {
    setSelectedArtistId(artistId);
    setIsLoadingArtistDetails(true);
    setValidationErrorMessage("");
    apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/spotify/artist/${artistId}`,
    })
      .then((response: SpotifyArtistGetResponse) => {
        const dto = response.artist;
        if (dto?.id == null) {
          throw new Error("Unexpected artist response shape");
        }
        setResult(JSON.stringify(mapArtistDtoToLegacy(dto)));
      })
      .catch((error: unknown) => {
        console.error("Error fetching Spotify artist details:", error);
        setValidationErrorMessage(String(t("songs:link-spotify-invalid-url-error")));
        setSelectedArtistId(undefined);
      })
      .finally(() => setIsLoadingArtistDetails(false));
  }, [t]);

  const handleSpotifyURLSubmit = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    const artistId = extractSpotifyArtistId(spotifyURL);

    if (artistId == null) {
      console.error("Invalid Spotify URL, URI, or artist ID");
      setValidationErrorMessage(String(t("songs:link-spotify-invalid-url-error")));
      setLoading(false);
      return;
    }

    try {
      const responseJson = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/artist/${artistId}`,
      }) as SpotifyArtistGetResponse;
      const dto = responseJson.artist;
      if (dto?.id == null) {
        throw new Error("Unexpected artist response shape");
      }
      setResult(JSON.stringify(mapArtistDtoToLegacy(dto)));
      setLoading(false);
      setValidationErrorMessage("");
      setFetchMethod("searchWithURL");
    }
    catch (error) {
      console.error("Error fetching artist by ID:", error);
      setLoading(false);
    }
  };

  if (flags["pd-spotify-link-account-feature"] !== true)
    return null;

  return (
    <div
      className={`${
        isTunecore
          ? "bg-canvas-inverse text-white font-tunecore"
          : "bg-layer-raised text-ink-primary "
      } flex items-center flex-col p-4 rounded-lg`}
    >
      <div className="inline-flex flex-row-reverse gap-2 mt-4">
        <Heading as="h3">{title}</Heading>
        <FaSpotify className="text-4xl text-[#1DB954]" />
      </div>
      <p className={`mt-4 ${!isTunecore ? "font-sentric-alt" : ""}`}>{desc}</p>
      <Tabs
        className="flex flex-col w-full mt-4"
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          setResult(null);
          setSelectedArtistId(undefined);
          setValidationErrorMessage("");
          setSpotifyURL("");
        }}
      >
        <TabsList className="w-5/6 lg:w-3/4 self-center">
          <TabsTrigger
            value="searchWithURL"
            className="min-h-[52px] xl:h-[45px]"
            style={activeTab === "searchWithURL" ? spotifyActiveTabStyle : undefined}
          >
            {t("songs:link-spotify-tab-header-link")}
          </TabsTrigger>
          <TabsTrigger
            value="searchWithName"
            className="min-h-[52px] xl:h-[45px]"
            style={activeTab === "searchWithName" ? spotifyActiveTabStyle : undefined}
          >
            {t("songs:link-spotify-tab-header-name")}
          </TabsTrigger>
        </TabsList>
        <TabsContent className="w-full p-4 lg:p-8" value="searchWithURL">
          <div className="flex flex-col">
            <p className={!isTunecore ? "font-sentric-alt" : ""}>
              {t("songs:link-spotify-url-instructions")}
              {" "}
              <span>
                <a
                  className="underline hover:scale-105"
                  href="https://support.spotify.com/us/artists/article/finding-your-artist-url/"
                >
                  {t("songs:link-spotify-instructions-link")}
                </a>
              </span>
            </p>
            {result == null && (
              <>
                <FormInput
                  type="text"
                  disabled={loading}
                  placeholder={String(t("songs:link-spotify-paste-url"))}
                  value={spotifyURL}
                  onChange={e => setSpotifyURL(e.target.value)}
                  className="w-full mt-4 mb-4"
                />
                <div className="self-end">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => { void handleSpotifyURLSubmit(e); }}
                    disabled={loading || spotifyURL === ""}
                    label={loading ? `${String(t("common:loading"))}...` : String(t("songs:link-spotify-fetch-profile"))}
                  />
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent className="p-2 lg:p-8" value="searchWithName">
          <div className="text-ink-primary">
            <Combobox
              data={artistOptions}
              value={selectedArtistId}
              onValueChange={handleSelectArtist}
              onSearchChange={handleArtistSearch}
              isLoading={isSearchingArtists || isLoadingArtistDetails}
              placeholder={String(t("songs:link-spotify-account"))}
              searchPlaceholder={String(t("songs:link-spotify-account"))}
              triggerClassName="w-full"
            />
          </div>
        </TabsContent>
      </Tabs>

      {(loading || isLoadingArtistDetails) && <ArtistCardSkeleton isTunecore={isTunecore} />}

      {parsedResult != null && (
        <div
          style={
            isTunecore
              ? {
                  background: "linear-gradient(to bottom right, #a6a6a6, #505050)",
                }
              : {
                  background: "#FFF",
                }
          }
          className={`${
            isTunecore
              ? "text-white"
              : "text-ink-primary "
          } rounded-lg shadow-lg flex mt-4 flex-col sm:w-80`}
        >
          <div className="mx-4 mt-4">
            {parsedResult.images.length > 0
              ? (
                  <img
                    height={160}
                    width={160}
                    className="object-cover w-full bg-gray-300 rounded-lg max-h-52"
                    alt={parsedResult.name}
                    src={parsedResult.images[0].url}
                  />
                )
              : (
                  <div className="w-full bg-gray-300 rounded-lg max-h-52" />
                )}
          </div>
          <div className="flex flex-col justify-between p-4">
            <Heading as="h3" className="self-center text-xl md:text-2xl">
              {parsedResult.name}
            </Heading>

            <div className="flex flex-col-reverse items-center justify-between gap-4 mt-4 md:flex-row md:gap-0">
              <Button
                variant="link"
                label={String(t("common:remove"))}
                onClick={() => {
                  setResult(null);
                  setSelectedArtistId(undefined);
                  setSpotifyURL("");
                }}
              />
              <Button
                variant="secondary"
                disabled={pending || parsedResult == null}
                id="btnRecordingFormAddRecording"
                onClick={addSpotify}
                className="md:self-end"
                label={String(t("songs:link-spotify-link-account-button"))}
              />
            </div>
          </div>
        </div>
      )}
      <FormError
        mutationError={mutationErrorMessage}
        validationError={validationErrorMessage}
      />
    </div>
  );
}

export default SocialSpotify;
